/**
 * TEST-ONLY sign-in for the admin app (apps/api admin-e2e-auth.ts): local
 * tests and walkthrough recordings open the app with a signed test token in
 * the URL fragment (`#e2eToken=`) and act as the seeded test staff user,
 * without a Clerk sign-in. The admin twin of mybcecloud's e2e-session.ts.
 *
 * Two gates keep it out of real use:
 * 1. Build time: compiled in only when the dev server runs with
 *    `VITE_E2E_AUTH=1`. The flag is replaced statically, so a production
 *    bundle cannot read the fragment at all.
 * 2. The API: the token signs in only on a local API started with
 *    `E2E_AUTH_SECRET`; every deployed API refuses to boot with it.
 */

const E2E_TOKEN_KEY = 'bce.adminE2eToken';
const FRAGMENT_PARAM = 'e2eToken';
const E2E_AUTH_ON = '1';

/** Whether this build accepts test sign-in at all. */
export function isE2EAuthEnabled(): boolean {
  return import.meta.env.VITE_E2E_AUTH === E2E_AUTH_ON;
}

/** The test token in a location hash, or null. */
export function parseE2ETokenFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const token = new URLSearchParams(raw).get(FRAGMENT_PARAM);
  return token && token.length > 0 ? token : null;
}

/**
 * Stores a test token arriving in the fragment for this tab and strips it
 * from the address bar. Does nothing unless the build has test sign-in on.
 */
export function consumeE2ETokenFromLocation(enabled: boolean = isE2EAuthEnabled()): void {
  if (!enabled) return;
  const token = parseE2ETokenFragment(window.location.hash);
  if (!token) return;
  try {
    window.sessionStorage.setItem(E2E_TOKEN_KEY, token);
  } catch {
    return;
  }
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

/** The stored test token, or null when there is no test session. */
export function getE2EToken(enabled: boolean = isE2EAuthEnabled()): string | null {
  if (!enabled) return null;
  try {
    return window.sessionStorage.getItem(E2E_TOKEN_KEY);
  } catch {
    return null;
  }
}
